import { IS_B2B } from "../config/constants";
import { isAuthenticated } from "../lib/auth";
import { checkInApp, checkInWeb } from "./checkurl";
import { logoutThenNavigate, removeFromLocalStorage } from "./logoututil";

// Read once by AgentLoginMenu after the redirect, to show "session expired".
export const B2B_SESSION_EXPIRED_FLAG = "b2bSessionExpired";

/**
 * A B2B user whose token has expired.
 *
 * Expiry used to remove only `expires_in`. isAuthenticated() then went false,
 * so every API call switched to the guest endpoint and priced at the B2C rate,
 * while `is_b2b` stayed "yes" - and the sales channel and payment page, which
 * read that flag directly, still treated the user as B2B. The booking ended up
 * tagged B2B Portal with a B2C-priced product.
 */
export const isB2BSessionExpired = () =>
  !window.location.search.includes("miniapp") &&
  localStorage.getItem(IS_B2B) === "yes" &&
  !isAuthenticated();

const homeFor = (pathname, search) => {
  const segment = pathname.split("/")[1];
  const lang = segment && segment.length === 2 ? segment : "en";
  return checkInApp(pathname)
    ? `/${lang}/in-app${search}`
    : checkInWeb(pathname)
    ? `/${lang}/in-web${search}`
    : `/${lang}${search}`;
};

/**
 * Ends an expired B2B session properly: clears every auth/role key and the
 * booking in progress, then does a full page load. The full load matters -
 * config/typecheck.js computes IS_B2B_VAL, CLASS_VALUE_ID and friends once at
 * import time, so without it they keep saying "B2B" for the rest of the page.
 *
 * Returns true when it acted.
 */
export const endExpiredB2BSession = () => {
  if (!isB2BSessionExpired()) return false;
  try {
    sessionStorage.setItem(B2B_SESSION_EXPIRED_FLAG, "yes");
  } catch (err) {
    // storage unavailable - the redirect still happens, just without the modal
  }
  logoutThenNavigate(homeFor(window.location.pathname, window.location.search));
  return true;
};

/**
 * Startup variant: a stale B2B session found before the app renders (e.g. the
 * user reloaded after expiry). Must run before config/typecheck.js is
 * evaluated - see sessionGuardBoot.js. The localStorage keys are removed
 * synchronously either way, so those constants come out as guest. Off the
 * home page we still redirect, because the booking in progress was just
 * cleared and was priced for B2B.
 */
export const purgeExpiredB2BSessionOnBoot = () => {
  if (!isB2BSessionExpired()) return;
  try {
    sessionStorage.setItem(B2B_SESSION_EXPIRED_FLAG, "yes");
  } catch (err) {
    // storage unavailable
  }
  const { pathname, search } = window.location;
  const home = homeFor(pathname, "");
  if (pathname === "/" || pathname.replace(/\/$/, "") === home) {
    removeFromLocalStorage();
  } else {
    logoutThenNavigate(homeFor(pathname, search));
  }
};
